import {useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import {useSnackbar} from "notistack";
import {
	COURSES_QUERY_KEYS,
	useSelfEnrollInCourse,
	useWhoAmI
} from "@/app2/shared/data";

/**
 * Course Enrollment Hook (Feature layer)
 *
 * Handles enrolling the authenticated user in a course and redirecting to the course page.
 * Uses useSelfEnrollInCourse from the data layer; adds feature-specific UI logic (snackbar, navigation).
 */
export const useCourseEnrollment = (courseSlug: string, courseId: number) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const {enqueueSnackbar} = useSnackbar();
	const {data: userData} = useWhoAmI();
	const userId = userData?.userId;

	const {mutate: selfEnroll, isPending: isEnrolling} = useSelfEnrollInCourse(courseId, userId);

	const enrollInCourse = useCallback(() => {
		if (!userId) {
			enqueueSnackbar("Unable to enroll: User not authenticated", {
				variant: "error",
				autoHideDuration: 5000
			});
			return;
		}

		selfEnroll(undefined, {
			onSuccess: () => {
				enqueueSnackbar("Successfully enrolled in course!", {
					variant: "success",
					autoHideDuration: 3000
				});

				// Invalidate course page caches (data layer handles enrollments, user feed, enrolled courses, search)
				queryClient.invalidateQueries({
					queryKey: [COURSES_QUERY_KEYS.DETAILS, courseSlug]
				});
				queryClient.invalidateQueries({
					queryKey: [COURSES_QUERY_KEYS.CURRICULUM, courseSlug]
				});

				setTimeout(() => {
					navigate(`/course/${courseSlug}`);
				}, 500);
			},
			onError: (error: any) => {
				console.error("Failed to enroll in course:", error);
				const errorMessage =
					error.response?.data?.message || "Failed to enroll in course";
				enqueueSnackbar(errorMessage, {
					variant: "error",
					autoHideDuration: 5000
				});
			}
		});
	}, [userId, selfEnroll, enqueueSnackbar, queryClient, courseSlug, navigate]);

	return {
		enrollInCourse,
		isEnrolling
	};
};

